import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { FileTypes } from '@guryou/core';
import {
  DefaultText,
  ExpandMoreIcon,
  FlexRow,
  IconButton,
  ImgIcon,
  openModal,
  PdfIcon,
  PlayIcon,
  SmallGrayText,
  SmallText,
  VisibilityIconOff,
  VisibilityIconOn,
} from '@guryou/html-components';
import { borderDefinition } from '@guryou/html-theme';
import { getCustomerPublicFile, markFileSeenUnseen } from '@guryou/redux';
import { printPercent } from '@guryou/utils/build/stringUtils';
import { Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core';

const checkIfLocked = (free, allowPreview) => {
  if (allowPreview) {
    return false;
  }
  return !free;
};

const getPercentage = (total, seen) => {
  if (seen === 0 || total === 0) {
    return printPercent(0);
  }
  const res = (seen / total) * 100;
  return printPercent(res);
};

const VideoCourseAccordion = React.memo(({ section, allowPreview }) => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState(null);
  const [files, setFiles] = useState(section.files || []);
  const dispatch = useDispatch();

  const handleChange = panel => (_, newExpanded) => {
    setActiveSection(newExpanded ? panel : false);
  };

  const handleFileSeenUnseen = (id, value) => {
    dispatch(markFileSeenUnseen(id, value));
    setFiles(prev => prev.map(x => (x.id === id ? { ...x, seen: value } : x)));
  };

  const handlePreviewFile = (fileType, id, free, providerId, transcoded, seen) => {
    if ((transcoded || [FileTypes.document, FileTypes.image].includes(fileType)) && !checkIfLocked(free, allowPreview)) {
      dispatch(getCustomerPublicFile(id, +providerId));
      if (!seen && allowPreview) {
        handleFileSeenUnseen(id, true);
      }
      if (fileType !== FileTypes.document) {
        dispatch(
          openModal(
            'myCustomModal',
            { items: files, active: id, providerId: +providerId, allowPreview, handleFileSeenUnseen: handleFileSeenUnseen },
            section.sectionName
          )
        );
      }
    }
  };

  const sectionFilesSeen = (files || []).filter(x => x.seen).length;
  const totalSectionFiles = (files || [])?.length;

  return (
    <div style={{ width: '100%', border: '1px solid lightgray', margin: '2px' }}>
      <Accordion onChange={handleChange(section.sectionId)} expanded={activeSection === section.sectionId}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <FlexRow alignItems="center" justifyContent="space-between">
            <DefaultText padding={2} fontWeight={700}>
              {section.sectionName}
            </DefaultText>
            <SmallGrayText>
              {totalSectionFiles} files {allowPreview && `| ${getPercentage(totalSectionFiles, sectionFilesSeen)}`}
            </SmallGrayText>
          </FlexRow>
        </AccordionSummary>
        {activeSection === section.sectionId && (
          <AccordionDetails style={{ display: 'block' }}>
            {files.map((x, i) => (
              <FlexRow xs={12} key={i} borderTop={borderDefinition()} margin={0} padding={3} justifyContent="space-between">
                <FlexRow xs={9.2} md={10.2} lg={10.2} alignItems="center">
                  {x.type === FileTypes.document ? <PdfIcon /> : x.type === FileTypes.image ? <ImgIcon /> : <PlayIcon />}
                  <DefaultText overflow="hidden" marginLeft={2} key={`file_${i}`}>
                    {x.title || x.name}
                  </DefaultText>
                </FlexRow>

                <FlexRow xs={2.6} md={1.6} lg={1.6} alignItems="center" justifyContent={allowPreview ? 'baseline' : 'end'}>
                  {FileTypes.video === x.type && !x.transcoded ? null : checkIfLocked(x.free, allowPreview) ? (
                    <SmallGrayText>Locked</SmallGrayText>
                  ) : (
                    <FlexRow justifyContent={allowPreview ? 'space-between' : 'end'} alignItems="center">
                      {allowPreview &&
                        (x.seen ? (
                          <IconButton size="small" tooltip="Mark unseen" onClick={() => handleFileSeenUnseen(x.id, false)}>
                            <VisibilityIconOn style={{ color: 'green', cursor: 'pointer' }} />
                          </IconButton>
                        ) : (
                          <IconButton size="small" disabled={true}>
                            <VisibilityIconOff style={{ color: 'lightgray' }} />
                          </IconButton>
                        ))}
                      <SmallText
                        textDecoration="underline"
                        onClick={() => handlePreviewFile(x.type, x.id, x.free, section.providerId, x.transcoded, x.seen)}
                      >
                        {t('GENERAL.preview')}
                      </SmallText>
                    </FlexRow>
                  )}
                </FlexRow>
              </FlexRow>
            ))}
          </AccordionDetails>
        )}
      </Accordion>
    </div>
  );
});

export default VideoCourseAccordion;
